import { useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import * as Types from 'types';
import useAssessmentStore from 'graph/state/query-hooks/use-assessment';
import { useFindOneStudentAssessmentStore } from 'graph/state/query-hooks/use-student-assessment';
import QuestionSection from './components/QuestionSection';
import useAssessmentReportStore from 'graph/state/query-hooks/use-assessment-report';
import DomainSelectionList from './components/DomainSelectionList';
import OverviewForm from './components/OverviewForm';
import { buildDomains, buildInitialAssessmentValues } from './assessment-util';

const t = (key) => translate('ui.assessments', key);

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const Assessment = (): React.ReactElement => {
  const navigate = useNavigate();
  const { id } = useParams();
  const query = useQuery();

  const studentOrUndefined = query.get('studentId');
  if (typeof id === 'undefined' || typeof studentOrUndefined === 'undefined') {
    console.log('missing params', id, studentOrUndefined);
    navigate('/home');
  }
  const assessmentId = parseInt(id!);
  const studentId = parseInt(studentOrUndefined!);
  const classId = query.get('classroomId');

  const [activeStep, setActiveStep] = useState(0);
  const [activeSubStep, setActiveSubStep] = useState(0);
  const classes = useStyles();

  const [studentAssessmentData, studentAssessmentDispatch] = useFindOneStudentAssessmentStore(
    assessmentId,
    studentId
  );

  const [assessmentStore] = useAssessmentStore({ where: { id: assessmentId } });
  const [assessmentReport] = useAssessmentReportStore({
    where: { assessmentId: Number(assessmentId), studentId: Number(studentId) },
  });

  const studentAssessment = studentAssessmentData.studentAssessment;

  const domains = buildDomains(assessmentStore.assessment);
  const validationSchema = {};
  const initialValues: { [key: string]: any } = assessmentStore.assessment
    ? buildInitialAssessmentValues(
        studentAssessment?.outcomes,
        assessmentStore.assessment as Types.Assessment,
        studentId,
        validationSchema
      )
    : {};

  const handleNext = () => {
    studentAssessmentDispatch.studentAssessmentRefetch();
    const maxSubStep = domains[activeStep].steps;
    if (activeStep === 0) {
      setActiveStep(activeStep + 1);
    } else if (activeStep + 1 === domains.length && activeSubStep + 1 === maxSubStep) {
      navigate(
        `/class/${classId}?tabSelection=${
          assessmentStore.assessment?.type === Types.AssessmentType.Benchmark ? 'Benchmark' : 'Unit'
        }`,
        { replace: true }
      );
    } else if (activeSubStep + 1 == maxSubStep) {
      setActiveSubStep(0);
      setActiveStep(activeStep + 1);
    } else {
      setActiveSubStep(activeSubStep + 1);
    }
  };

  const handleBack = () => {
    if (activeSubStep === 0) {
      setActiveStep(activeStep - 1);
      setActiveSubStep(domains[activeStep - 1].steps - 1);
    } else {
      setActiveSubStep(activeSubStep - 1);
    }
  };

  const handleStepSkip = (step: number) => {
    setActiveStep(step);
    setActiveSubStep(0);
  };

  return (
    <div className={classes.mainContainer} style={{ flexGrow: 1 }}>
      <Grid container>
        <Grid container item xs={12} className={classes.pageHeader}>
          <Grid item xs={6}>
            <Typography variant="h1" component="h1">
              {t('assessment')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            className={classes.dFlex}
            alignItems="flex-end"
            justifyContent="flex-end"
          >
            <RouterLink
              to={`/class/${classId}?tabSelection=${
                assessmentStore.assessment?.type === Types.AssessmentType.Benchmark
                  ? 'Benchmark'
                  : 'Unit'
              }`}
              className={classes.secondaryLink}
            >
              {t('scores')}
            </RouterLink>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Grid container className={classes.section} style={{ flexGrow: 1 }}>
        <Grid item container xs={12}>
          {!assessmentStore.isLoading && (
            <>
              {!studentAssessmentData.loading && activeStep == 0 && (
                <OverviewForm
                  studentAssessment={studentAssessment}
                  studentId={studentId}
                  assessmentId={assessmentId}
                  next={handleNext}
                />
              )}
              {!studentAssessmentData.loading && activeStep != 0 && (
                <Grid item xs={8}>
                  <QuestionSection
                    assessmentId={assessmentId}
                    studentId={studentId}
                    section={
                      assessmentStore.assessment!.assessmentDomains![activeStep - 1]
                        .assessmentSections[activeSubStep]
                    }
                    initialValues={
                      initialValues[
                        assessmentStore.assessment!.assessmentDomains![activeStep - 1]
                          .assessmentSections[activeSubStep].id
                      ]
                    }
                    validationSchema={
                      validationSchema[
                        assessmentStore.assessment!.assessmentDomains![activeStep - 1]
                          .assessmentSections[activeSubStep].id
                      ]
                    }
                    outcomes={studentAssessment?.outcomes}
                    submit={handleNext}
                    back={handleBack}
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <DomainSelectionList
                  domains={domains}
                  selectedIndex={activeStep}
                  assessmentReport={assessmentReport.assessmentReport}
                  activeSubStep={activeSubStep}
                  callbackFn={handleStepSkip}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default Assessment;

const useStyles = makeStyles((theme: Theme) => {
  return {
    dFlex: {
      display: 'flex',
    },
    pb4: {
      paddingBottom: theme.spacing(4),
    },
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
      display: 'flex',
      flexDirection: 'column',
    },
    pageHeader: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
    },
    section: {
      backgroundColor: 'white',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(8),
      padding: theme.spacing(4),
      border: `1px solid ${(theme.palette as any).grey[600]}`,
      '& .MuiGrid-grid-xs-8': {
        display: 'flex',
        flexDirection: 'column',
        paddingRight: theme.spacing(4),
        '& form': {
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },
    sectionHeader: {
      color: theme.palette.primary.main,
    },
    btnFilled: {
      borderRadius: '100px',
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(4),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(4),
      lineHeight: '1rem',
    },
    vertTab: {
      height: '100%',
      margin: '0',
      borderLeft: `3px solid ${(theme.palette as any).grey[400]}`,

      '&>li': {
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginLeft: '-3px',
        borderLeft: '3px solid transparent',

        '&>a': {
          color: theme.palette.black.main,
        },
      },
    },
    formSection: {
      marginTop: theme.spacing(4),
    },
    footerGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    loading: {
      opacity: 0.4,
    },
    centered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
    },
    highlighted: {
      color: '#DC20AD',
    },
    ul: {
      listStyle: 'none',
      padding: 0,
    },
    secondaryLink: {
      textDecoration: 'none !important',
      color: theme.palette.black.main + ' !important',
      marginLeft: theme.spacing(4),
      fontFamily: 'HWT Futura',
      '&:hover': {
        textDecoration: 'underline !important',
      },
    },
  };
});
